import CollectInputsLink from "@/components/OfferPlacement.collect-inputs-link";
import type {
  CollectInputsPresentationProps,
  CollectInputsPreset,
} from "@/components/OfferPlacement.collect-inputs";
import { offerPresentationId } from "@/lib/analytics-offer-milestones";
import { buildReportInputHref, type ReportInputBirthContext } from "@/lib/report-input-href";
import { resolvePageReportOffer } from "@/lib/page-report-offer";
import type { SEOIntent, SEOReportPlanId } from "@/lib/seo-intents";

/**
 * A page's next-step report section. The plan comes from the governed
 * source -> intent map (rule 40) unless the page pins one.
 */
export function pageReportBridgeOffer({
  source,
  planId,
  intent,
  placement = `${source}_report_bridge`,
  language = "en",
  birthContext,
}: {
  source: string;
  planId?: SEOReportPlanId;
  intent?: SEOIntent;
  placement?: string;
  language?: "en" | "hi";
  birthContext?: ReportInputBirthContext;
}): CollectInputsPreset {
  const governed = resolvePageReportOffer(source);
  const resolvedPlanId = planId ?? governed.planId;
  const resolvedIntent = intent ?? governed.intent;
  return {
    mode: "collect_inputs",
    presentation: CollectInputsSection,
    presentationKey: offerPresentationId({
      actionMode: "input_collection",
      planId: resolvedPlanId,
      placement,
      sourcePath: source,
    }),
    gate: true,
    placement,
    planId: resolvedPlanId,
    intent: resolvedIntent,
    sourcePath: source,
    language,
    href: buildReportInputHref({
      sourcePath: source,
      intent: resolvedIntent,
      planId: resolvedPlanId,
      language,
      birthContext,
    }),
    ctaLabel: language === "hi" ? "कुंडली पर आधारित रिपोर्ट पाएं" : "Get chart-backed report",
  };
}

/** The inline next-step section presentation of OfferPlacement `mode="collect_inputs"`. */
function CollectInputsSection(props: CollectInputsPresentationProps) {
  const { language } = props;
  return (
    <section
      className={
        props.className ??
        "mt-6 rounded-lg border border-[var(--ish-border)] bg-[var(--ish-bg-card)] p-5 text-center shadow-sm"
      }
    >
      {props.impression}
      <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-intent-brand">
        {language === "hi" ? "अगला चरण" : "Next Step"}
      </p>
      <h2 className="mt-2 font-serif text-[22px] leading-tight text-[var(--ish-dark)]">
        {props.headline}
      </h2>
      <p className="mx-auto mt-2 max-w-xl text-body-small leading-6 text-[var(--ish-text-muted)]">
        {props.subhead}
      </p>
      {props.gated(
        <CollectInputsLink
          href={props.href}
          planId={props.planId}
          sourcePath={props.sourcePath}
          intent={props.intent}
          placement={props.placement}
          presentationId={props.presentationId}
          className="mt-4 inline-flex min-h-11 items-center justify-center rounded-md bg-[var(--ish-accent)] px-5 text-body font-semibold text-white"
        >
          {props.ctaLabel} · ₹{props.priceInr.toLocaleString("en-IN")}
        </CollectInputsLink>
      )}
    </section>
  );
}
